import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNGConfig } from 'primeng/api';
import { Observable } from 'rxjs';
import { getPrimeNgTranslation } from './primeng-translations';

export type SupportedLang = 'pt-BR' | 'es';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly supported: SupportedLang[] = ['pt-BR', 'es'];
  private primeNgListenerAtivo = false;

  constructor(
    private translate: TranslateService,
    private primeNGConfig: PrimeNGConfig,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private doc: Document
  ) {}

  init(): Promise<void> {
    const isBrowser = isPlatformBrowser(this.platformId);

    // Usa localStorage para persistir idioma entre abas e sessoes
    // (sessionStorage do SafeStorageService nao compartilha entre abas)
    const savedRaw = isBrowser ? localStorage.getItem('lang') : null;

    // Default sempre espanhol (es) quando nao ha idioma salvo.
    // O usuario pode trocar pelo dropdown do header a qualquer momento.
    const lang = this.normalize(savedRaw || 'es');

    this.translate.addLangs(this.supported);
    this.translate.setDefaultLang(lang);

    if (isBrowser) this.setHtmlLang(lang);

    // Configura traducoes do PrimeNG (calendario, filtros, confirm, etc.) e
    // garante que elas sejam atualizadas sempre que o idioma mudar.
    this.aplicarPrimeNgTranslation(lang);
    this.registrarListenerPrimeNg();

    // Aguarda o carregamento do arquivo JSON de traducao antes de liberar o app.
    // Sem isso, translate.instant() retorna a chave em vez do texto traduzido
    // porque o APP_INITIALIZER nao espera o HTTP request terminar.
    return new Promise<void>((resolve) => {
      this.translate.use(lang).subscribe({
        next: () => resolve(),
        error: () => resolve(), // mesmo em erro, libera o app
      });
    });
  }

  /** Aceita 'pt', 'pt-BR', 'es', 'es-ES', etc. */
  setLanguage(lang: string): void {
    const normalized = this.normalize(lang);
    // Mantem o idioma de FALLBACK (defaultLang) alinhado ao idioma ativo.
    // Sem isso, o fallback ficava preso no idioma inicial (default 'es'): quem
    // abria o app em espanhol e trocava para portugues continuava vendo QUALQUER
    // chave ausente no pt-BR.json em espanhol (ex.: dialogos de aprovacao).
    // O init() ja fixa os dois juntos; aqui preservamos esse invariante na troca.
    this.translate.setDefaultLang(normalized);
    this.translate.use(normalized);
    if (isPlatformBrowser(this.platformId)) {
      this.setHtmlLang(normalized);
      // Salva em localStorage para persistir entre abas e sessoes
      localStorage.setItem('lang', normalized);
    }
  }

  getCurrent(): SupportedLang {
    const cur = (this.translate.currentLang || this.translate.getDefaultLang()) as string;
    return this.normalize(cur);
  }

  /** Alias para compatibilidade com componentes que chamam current() */
  current(): SupportedLang {
    return this.getCurrent();
  }

  /** Converte qualquer variação para os dois suportados internamente */
  private normalize(input: string): SupportedLang {
    const l = (input || '').toLowerCase();
    if (l.startsWith('pt')) return 'pt-BR';
    if (l.startsWith('es')) return 'es';
    return 'es'; // fallback
  }

  private setHtmlLang(lang: SupportedLang): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.doc?.documentElement?.setAttribute('lang', lang);
  }

  /**
   * Aplica a traducao do PrimeNG para o idioma informado.
   * Usado pelo p-calendar (Hoje/Limpar, nomes de meses/dias), p-table (filtros),
   * p-confirmDialog (Sim/Nao), p-fileUpload (Escolher/Enviar/Cancelar), etc.
   */
  private aplicarPrimeNgTranslation(lang: SupportedLang): void {
    const translation = getPrimeNgTranslation(lang);
    this.primeNGConfig.setTranslation(translation);
  }

  /**
   * Atualiza as traducoes do PrimeNG sempre que o ngx-translate trocar de idioma.
   * Registra apenas uma vez (guard em primeNgListenerAtivo).
   */
  private registrarListenerPrimeNg(): void {
    if (this.primeNgListenerAtivo) return;
    this.primeNgListenerAtivo = true;
    this.translate.onLangChange.subscribe(({ lang }) => {
      this.aplicarPrimeNgTranslation(this.normalize(lang));
    });
  }
}
